// 役員ブログ・事業部ブログで使う WP カテゴリIDの解決
// WPのカテゴリIDは本番・テスト環境で異なるが、スラッグは共通。
// そのため各JSONには表示用の情報とスラッグ（"id"）だけを持たせ、
// 表示時にWPのカテゴリ一覧を取得してスラッグからIDを求める（IDのハードコード・環境別JSONは不要）
import BoardVoiceJson from './board-voice.json';
import DepartmentBlogJson from './departmentBlog.json';
import DepartmentBlogTwoJson from './departmentBlog202507.json';

type Term = { id: number; slug: string; parent: number };

type BoardVoiceItem = (typeof BoardVoiceJson)[number] & {
  catId: string | null;
  dispno: number | null;
  preparing: boolean;
};
type DepartmentItem<T> = T & { parent: string; deparmentId: string };

const urlBaseBigWig = process.env.REACT_APP_WP_REST_API_PATH_BIGWIG;
const urlBaseDepartmentBlog = process.env.REACT_APP_WP_REST_API_PATH_DEPARTMENTBLOG;

// 同じURLのカテゴリ一覧は1回だけ取得する（ヘッダーの新着表示とページ本体で共有）
const termCache: { [url: string]: Promise<Term[]> } = {};

function fetchTerms(urlBase: string | undefined, taxonomy: string, jti: string): Promise<Term[]> {
  const url = `${urlBase}wp-json/wp/v2/${taxonomy}?per_page=100&_fields=id,slug,parent&zenken=${jti}`;
  if (!termCache[url]) {
    termCache[url] = fetch(url)
      .then((res) => res.json())
      .then((data) => {
        // 認証エラー等で一覧が取れなかった場合は、次回取り直せるようキャッシュしない
        if (!Array.isArray(data)) {
          delete termCache[url];
          return [];
        }
        return data as Term[];
      })
      .catch(() => {
        delete termCache[url];
        return [];
      });
  }
  return termCache[url];
}

function warnMissing(slug: string, taxonomy: string) {
  console.warn(`カテゴリが見つかりません（${taxonomy} / スラッグ「${slug}」）。WPのスラッグとJSONの "id" が一致しているか確認してください。`);
}

// 役員ブログ：JSONの "id"（スラッグ）からカテゴリID（catId）を求める
// ・icon が空の枠は表示しない（追加準備用）
// ・id（スラッグ）が空の役員は「準備中」（preparing: true）としてアイコンのみ表示する
// ・id があるのにWPにカテゴリが見つからない役員は表示しない（コンソールに警告）
// ・dispno は公開中の役員だけに、board-voice.json の並び順で自動で振る（準備中の役員が間に入っても番号はずれない）
export async function loadBoardVoice(jti: string): Promise<BoardVoiceItem[]> {
  const terms = await fetchTerms(urlBaseBigWig, 'categories', jti);
  const list: BoardVoiceItem[] = [];
  let activeCount = 0;
  BoardVoiceJson.forEach((item) => {
    if (!item.icon) {
      return;
    }
    if (!item.id) {
      list.push({ ...item, catId: null, dispno: null, preparing: true });
      return;
    }
    const term = terms.find((t) => t.slug === item.id);
    if (!term) {
      warnMissing(item.id, 'categories');
      return;
    }
    activeCount++;
    list.push({ ...item, catId: String(term.id), dispno: activeCount, preparing: false });
  });
  return list;
}

// 事業部ブログ：JSONの "id"（事業本部のスラッグ）から、親カテゴリID（parent）と子カテゴリID（deparmentId、カンマ区切り）を求める
// アイコンの並び（iconDispNo）を崩さないよう、見つからない事業本部も残して空のIDにする
async function loadDepartment<T extends { id: string }>(list: T[], taxonomy: string, jti: string): Promise<DepartmentItem<T>[]> {
  const terms = await fetchTerms(urlBaseDepartmentBlog, taxonomy, jti);
  return list.map((item) => {
    const parent = terms.find((t) => t.slug === item.id);
    if (!parent) {
      warnMissing(item.id, taxonomy);
      return { ...item, parent: '', deparmentId: '' };
    }
    const childIds = terms.filter((t) => t.parent === parent.id).map((t) => t.id);
    return {
      ...item,
      parent: String(parent.id),
      // 子カテゴリがない場合は親カテゴリ自体の記事を対象にする
      deparmentId: childIds.length > 0 ? childIds.join(',') : String(parent.id),
    };
  });
}

// 事業部ブログ（旧組織 ～25.06）：通常の投稿カテゴリ
export function loadDepartmentBlog(jti: string) {
  return loadDepartment(DepartmentBlogJson, 'categories', jti);
}

// 事業部ブログ（25.07～）：カスタムタクソノミー division_blog_category
export function loadDepartmentBlogTwo(jti: string) {
  return loadDepartment(DepartmentBlogTwoJson, 'division_blog_category', jti);
}
